import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NotificationsService, Notification } from 'src/app/services/notifications.service';
import { SoundNotificationsService } from 'src/app/services/sound-notifications.service';
import { TcTicketService } from 'src/app/shared/services/ticket-center/tc-ticket.service';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-notifications-dropdown',
  templateUrl: './notifications-dropdown.component.html',
  styleUrls: ['./notifications-dropdown.component.scss'],
  standalone: false,
})
export class NotificationsDropdownComponent implements OnInit, OnDestroy {
  showNotifications = false;
  notifications: Notification[] = [];
  soundEnabled = true;
  private subscription: Subscription = new Subscription();
  private readonly MUTED_NOTIFICATIONS_KEY = 'muted-notifications';

  constructor(
    private notificationsService: NotificationsService,
    private soundNotificationsService: SoundNotificationsService,
    private ticketService: TcTicketService
  ) {}

  ngOnInit(): void {
    // Subscrever às mudanças nas notificações
    this.subscription.add(
      this.notificationsService.notifications$.subscribe(notifications => {
        this.notifications = notifications;
      })
    );

    // Subscrever às configurações de som
    this.subscription.add(
      this.soundNotificationsService.soundEnabled$.subscribe(enabled => {
        this.soundEnabled = enabled;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
    this.updateHeaderZIndex();

    // Verificar configuração de som no localStorage quando abrir o dropdown
    if (this.showNotifications) {
      this.checkSoundSettings();
      this.fillMissingTicketNumbers();
    }
  }

  /** Chamado ainda sem número mostra só o começo do UUID no cartão. */
  displayRef(ref: string): string {
    return UUID_PATTERN.test(ref) ? `${ref.slice(0, 8)}…` : ref;
  }

  /**
   * Chamado recém-criado ainda não tem número e a notificação guarda o UUID no lugar. Ao abrir a
   * lista, busca o número dos que continuam assim; sem nenhum nessa condição, não faz nada.
   */
  private async fillMissingTicketNumbers(): Promise<void> {
    const pending = this.notifications.filter(
      n => n.type === 'ticket' && UUID_PATTERN.test(n.ref) && (n.uuid || n.ref)
    );
    if (!pending.length) return;

    const numbers = new Map<string, string>();
    await Promise.all(
      [...new Set(pending.map(n => n.uuid || n.ref))].map(async ticketId => {
        try {
          const ticket: any = await this.ticketService.findById(ticketId);
          if (ticket?.ticket_number_teorema) {
            numbers.set(ticketId, String(ticket.ticket_number_teorema).padStart(10, '0'));
          }
        } catch {
          // Sem acesso ou sem rede: o cartão segue com o UUID reduzido.
        }
      })
    );

    const changes = pending
      .filter(n => numbers.has(n.uuid || n.ref))
      .map(n => ({ id: n.id, oldRef: n.ref, ref: numbers.get(n.uuid || n.ref)! }));
    if (!changes.length) return;

    this.moveMutedRefs(changes);
    await this.notificationsService.updateRefs(changes);
  }

  /** O silenciamento é por `ref`: acompanha o chamado quando o UUID vira número. */
  private moveMutedRefs(changes: { oldRef: string; ref: string }[]): void {
    try {
      const stored = localStorage.getItem(this.MUTED_NOTIFICATIONS_KEY);
      if (!stored) return;
      const muted: string[] = JSON.parse(stored);
      const renamed = muted.map(ref => changes.find(change => change.oldRef === ref)?.ref ?? ref);
      localStorage.setItem(this.MUTED_NOTIFICATIONS_KEY, JSON.stringify([...new Set(renamed)]));
    } catch {
      // Silenciamento é conveniência: se falhar, só volta a tocar som.
    }
  }

  closeNotifications() {
    this.showNotifications = false;
    this.updateHeaderZIndex();
  }

  private updateHeaderZIndex(): void {
    const layoutHeader = document.getElementById('layout-header');
    if (layoutHeader) {
      if (this.showNotifications) {
        layoutHeader.classList.add('bring-to-front');
      } else {
        layoutHeader.classList.remove('bring-to-front');
      }
    }
  }

  trackByNotificationId(index: number, notification: Notification): string {
    return notification.id;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const notificationContainer = target.closest('.notification-container');
    const dropdown = target.closest('.notifications-dropdown');

    if (!notificationContainer && !dropdown) {
      this.showNotifications = false;
      this.updateHeaderZIndex();
    }
  }

  openInTicketNewTab(notification: Notification) {
    if (!notification.ref) {
      return;
    }

    switch (notification.type) {
      case 'ticket':
        // Fim de execução de IA: o chamado abre já com a Análise por IA e o resultado.
        const aiRunId = notification.metadata?.aiRunId;
        window.open(
          `${window.location.origin}/v3ndor/chamados/${notification.ref}` +
            (aiRunId ? `?ai=${encodeURIComponent(aiRunId)}` : ''),
          '_blank'
        );
        break;
      case 'general':
        // Para notificações gerais, não faz nada no click
        break;
      default:
        // Para tipos não reconhecidos, não faz nada no click
        break;
    }
  }

  deleteNotification(notificationId: string): void {
    this.notificationsService.removeNotification(notificationId);
  }

  toggleSound(): void {
    this.soundNotificationsService.setSoundEnabled(!this.soundEnabled);
  }

  isMuted(notificationRef: string): boolean {
    try {
      const stored = localStorage.getItem(this.MUTED_NOTIFICATIONS_KEY);
      if (stored) {
        const mutedList = JSON.parse(stored);
        return mutedList.includes(notificationRef);
      }
      return false;
    } catch (error) {
      console.error('Erro ao verificar notificações silenciadas:', error);
      return false;
    }
  }

  toggleMuteNotification(notificationRef: string): void {
    try {
      const stored = localStorage.getItem(this.MUTED_NOTIFICATIONS_KEY);
      let mutedList: string[] = stored ? JSON.parse(stored) : [];

      if (mutedList.includes(notificationRef)) {
        mutedList = mutedList.filter(ref => ref !== notificationRef);
      } else {
        mutedList.push(notificationRef);
      }

      localStorage.setItem(this.MUTED_NOTIFICATIONS_KEY, JSON.stringify(mutedList));
    } catch (error) {
      console.error('Erro ao alternar silenciamento de notificação:', error);
    }
  }

  clearAllNotifications(): void {
    this.notificationsService.clearAllNotifications();
  }

  private checkSoundSettings(): void {
    try {
      const stored = localStorage.getItem('sound-notifications-enabled');
      if (stored !== null) {
        const enabled = JSON.parse(stored);
        if (enabled !== this.soundEnabled) {
          this.soundEnabled = enabled;
          // Atualizar o serviço se necessário
          this.soundNotificationsService.setSoundEnabled(enabled);
        }
      }
    } catch (error) {
      console.error('Erro ao verificar configurações de som:', error);
    }
  }
}
